import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banknote } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatMoeda } from '@fiodecobre/shared';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Resumo {
  qtd: number;
  total?: number;
  dias?: number;
  sessao_id?: number;
  caixa_nome?: string;
}

const POLLING_MS = 5 * 60 * 1000;

// "Tem recebimento esperando no Movimento de Caixa" (pedido dele, 2026-09-05: *"Precisa tambem
// criar uma pendencia pra mim quando tiver recebimento para ser fechado no Movimento de Caixa.
// Dessa forma nao vou esquecer de fecha-lo"*).
//
// O Movimento e' o unico caixa de dinheiro sem dono, entao e' o unico que ninguem fecha por
// rotina — as gavetas de gente fecham no fim do expediente. No dia do pedido a sessao dele
// estava aberta havia 17 dias com 15 recebimentos e R$ 1.692,00 dentro.
//
// ⚠️ Ela ACENDE quase todo dia, e isso e' o pedido, nao um defeito: o gesto que a apaga e'
// fechar o caixa. Diferente das outras faixas, o numero dela nunca vira zero sozinho — ou ele
// fecha, ou ela fica. Por isso a cor e' OURO e nao laranja: laranja e' das que pedem acao HOJE
// pra loja nao parar, e gastar o laranja aqui derrubaria a autoridade dele nas outras.
export function AlertaMovimentoCaixaBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [resumo, setResumo] = useState<Resumo | null>(null);

  const podeVer = usuario && ['admin', 'gerente'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<Resumo>('/api/caixa/movimento-a-fechar');
        if (!cancelado) setResumo(r);
      } catch {}
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  // `resumo &&` antes do teste: sem isso a faixa nasceria acesa em toda montagem, no intervalo
  // entre o render e a resposta — afirmando um estado que ainda nao foi medido.
  const ativo = !!(podeVer && resumo && resumo.qtd > 0);
  const recolhido = useAlertaFaixa(
    'movimento-a-fechar',
    ativo ? { rotulo: 'Fechar Movimento', bg: '#eab308', borda: '#a16207', qtd: resumo!.qtd } : null,
  );

  if (!ativo || recolhido || !resumo) return null;

  const dias = resumo.dias ?? 0;

  return (
    <div
      style={{
        background: '#eab308',
        borderLeft: '4px solid #a16207',
        color: '#000',
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
        cursor: 'pointer',
      }}
      onClick={() => navigate('/caixa', { state: { aba: 'sessoes' } })}
    >
      <Banknote size={16} />
      <span style={{ fontWeight: 600 }}>Movimento de Caixa:</span>
      <span>
        {resumo.qtd} recebimento{resumo.qtd === 1 ? '' : 's'} a fechar
        {resumo.total != null && <> · {formatMoeda(resumo.total)}</>}
        {' · '}
        {dias === 0 ? 'aberto hoje' : `aberto há ${dias} dia${dias === 1 ? '' : 's'}`}
      </span>
      <span style={{ marginLeft: 'auto', textDecoration: 'underline' }}>Ver e fechar</span>
    </div>
  );
}
